import React from 'react';
import {
  AppBar,
  Avatar,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Icon,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Pagination,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  type SxProps,
  type Theme
} from '@mui/material';
import { MedicalSuppliesService } from '../hooks';
import { extractApiErrorMessage, notifications } from '../utils';
import { useTranslation } from 'react-i18next';
import { ClearFilters, Loader, MedicalSupplyTypeSelect, SearchIconButton } from '../elements';
import { LoaderSize } from '../constants/constants';
import { MedicalSupplyType, MedicalSupplyViewModel } from '../models/injection';
import CloseIcon from '@mui/icons-material/Close';
import { V4_DIALOG_MD, V4_OUTLINED_DEFAULT, V4_SMALL_SCREEN } from '../theme/v4Compat';

interface MedicalSupplySelectModalProps {
  supply: MedicalSupplyViewModel | null;
  onChange: (supply: MedicalSupplyViewModel | null) => void;
  selectorId?: string;
  disableAny?: boolean;
  disabled?: boolean;
  /** Extra styles for the trigger button. */
  sx?: SxProps<Theme>;
  fullWidth?: boolean;
}

/** What a ref to this modal can call. */
export interface MedicalSupplySelectModalHandle {
  openModal: () => void;
}

const MedicalSupplySelectModal = React.forwardRef((props: MedicalSupplySelectModalProps, ref) => {
  const { supply, onChange, selectorId, disableAny, sx, disabled, fullWidth } = props;
  const { t } = useTranslation();
  const fullScreen = useMediaQuery(V4_SMALL_SCREEN);

  const medicalSuppliesService = new MedicalSuppliesService();

  const [supplies, setSupplies] = React.useState([] as MedicalSupplyViewModel[]);
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const [text, setText] = React.useState('');
  const [supplyType, setSupplyType] = React.useState(0);
  const [number, setNumber] = React.useState('');

  React.useImperativeHandle(
    ref,
    (): MedicalSupplySelectModalHandle => ({
      openModal() {
        setOpen(true);
      }
    })
  );

  // pagination
  const [page, setPage] = React.useState(1);
  const [total, setTotal] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };
  // * pagination

  const suppliesDisplayCount = 10;

  const loadMedicalSupplies = async () => {
    setIsBusy(true);

    medicalSuppliesService
      .getMedicalSupplies(page, suppliesDisplayCount, number, supplyType, 'popularity-desc')
      .then((result) => {
        setSupplies(result.data);
        setTotal(result.total);
        setLoaded(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const handleSelectorClick = () => {
    setOpen(true);
  };

  React.useEffect(() => {
    if (open && !loaded) {
      loadMedicalSupplies();
    }
  }, [open, loaded]);

  React.useEffect(() => {
    if (!supply) {
      setTextAny();
    } else {
      setTextBySupply(supply);
    }
  }, [supply]);

  const handleClose = () => {
    setOpen(false);
  };

  const setTextBySupply = (supply: MedicalSupplyViewModel) => {
    setText(supply.number);
  };

  const setTextAny = () => {
    setText(t('f-supp-any'));
  };

  const handleSupplySelectClick = (supply: MedicalSupplyViewModel) => {
    setTextBySupply(supply);
    setOpen(false);
    onChange(supply);
  };

  const handleSelectAny = () => {
    setOpen(false);
    setTextAny();
    onChange(null);
  };

  const clearFilters = () => {
    setSupplyType(0);
    setNumber('');
    setPage(1);
    setLoaded(false);
  };

  const renderFiltersSection = () => {
    return (
      // v4's layout, kept: a spacing-1 row pulled 4px left, each half padded 4px (5px below).
      <div className="-mx-1 mb-2 mt-4 box-border flex w-full flex-wrap px-2">
        <div className="box-border flex w-1/2 items-center p-1 pb-[5px]">
          <div className="flex w-full items-center">
            <MedicalSupplyTypeSelect
              allowAny
              fullWidth={true}
              value={supplyType}
              onSupplyTypeChange={(value) => setSupplyType(value)}
            />
          </div>
        </div>

        <div className="box-border flex w-1/2 items-center p-1 pb-[5px]">
          <div className="flex w-full items-center">
            <TextField
              label={t('f-com-number')}
              variant="outlined"
              size="small"
              fullWidth
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setLoaded(false);
                }
              }}
            />
            <ClearFilters onClick={clearFilters} />
            <SearchIconButton onClick={() => setLoaded(false)} />
          </div>
        </div>
      </div>
    );
  };

  const renderMedicalSupplySecondaryText = (supply: MedicalSupplyViewModel) => {
    return supply.producer
      ? `${MedicalSupplyType[supply.supplyType]} [${supply.producer}]`
      : MedicalSupplyType[supply.supplyType];
  };

  return (
    <>
      <Button
        size="medium"
        key={selectorId || 'supplySelectModalBtn'}
        fullWidth={fullWidth}
        variant="outlined"
        color="inherit"
        sx={[V4_OUTLINED_DEFAULT, ...(Array.isArray(sx) ? sx : [sx])]}
        disabled={disabled}
        endIcon={<Icon>{'medical_services'}</Icon>}
        onClick={handleSelectorClick}
      >
        {text}
      </Button>

      <Dialog
        onClose={handleClose}
        aria-labelledby="supply-select-title"
        fullWidth
        maxWidth="md"
        sx={V4_DIALOG_MD}
        open={open}
        fullScreen={fullScreen}
      >
        {fullScreen ? (
          <AppBar position="relative">
            <Toolbar>
              <div className="flex w-full justify-between">
                <div className="flex items-center">
                  <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
                    <CloseIcon />
                  </IconButton>
                  <Typography variant="h6">{t('f-supp-select-mod')}</Typography>
                </div>
              </div>
            </Toolbar>
          </AppBar>
        ) : (
          <DialogTitle id="supply-select-title">{t('f-supp-select-mod')}</DialogTitle>
        )}

        <DialogContent sx={{ p: 0 }}>
          {renderFiltersSection()}

          <Divider />

          <div className="h-170 overflow-auto">
            {isBusy && (
              <div className="flex justify-center items-center h-170">
                <Loader isLoading={true} size={LoaderSize.DEFAULT_SIZE} />
              </div>
            )}

            {!isBusy && loaded && (
              <>
                <div className="flex justify-end mr-2 mt-1">
                  <Typography>{t('f-search-results')}</Typography>
                  <div className="ml-1">
                    <Chip label={total} variant="outlined" size="small" id="totalChip" />
                  </div>
                </div>

                <List dense>
                  {supplies.map((supply) => (
                    <ListItemButton onClick={() => handleSupplySelectClick(supply)} key={supply.id}>
                      <ListItemAvatar>
                        <Avatar>{supply.number[0].toUpperCase() || '-'}</Avatar>
                      </ListItemAvatar>

                      <ListItemText primary={supply.number} secondary={renderMedicalSupplySecondaryText(supply)} />
                    </ListItemButton>
                  ))}

                  <ListItem id="pagination-li" sx={{ justifyContent: 'center' }}>
                    <Pagination
                      count={Math.ceil(total / suppliesDisplayCount)}
                      size="small"
                      color="primary"
                      showFirstButton
                      showLastButton
                      page={page}
                      onChange={handleChangePage}
                    />
                  </ListItem>
                </List>
              </>
            )}
          </div>
        </DialogContent>

        <DialogActions>
          {!fullScreen && (
            <Button onClick={handleClose} color="primary">
              {t('m-global-close')}
            </Button>
          )}
          <Button onClick={handleSelectAny} color="primary" disabled={disableAny}>
            {t('f-supp-any')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default MedicalSupplySelectModal;
